import { useAuth } from "@clerk/expo";
import * as Sentry from "@sentry/react-native";
import { AxiosError, create } from "axios";
import { useEffect, useMemo } from "react";

import { useLocalization } from "@/hooks/useLocalizationContext";
import { useShareToken } from "@/hooks/useShareTokenContext";
import { ErrorResponse } from "@/types/responses";

const useAxiosClient = (baseURL: string) => {
  const { getToken } = useAuth();
  const { i18n, locale } = useLocalization();
  const shareToken = useShareToken();

  const client = useMemo(() => {
    const instance = create({
      baseURL: `${process.env.EXPO_PUBLIC_API_URL}${baseURL}`,
    });

    instance.interceptors.request.use(async (config) => {
      const token = (await getToken()) ?? shareToken;
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    return instance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseURL, shareToken]);

  // Send the current language with every request. Re-register on locale change
  // so the header stays current without rebuilding (re-authenticating) the
  // memoized client.
  useEffect(() => {
    const interceptorId = client.interceptors.request.use((config) => {
      config.headers["Accept-Language"] = locale.replace("-", "_");
      return config;
    });

    return () => client.interceptors.request.eject(interceptorId);
  }, [client, locale]);

  // Translate API errors with the current i18n. Re-register the interceptor
  // whenever i18n changes so error messages always use the active locale.
  useEffect(() => {
    const interceptorId = client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ErrorResponse>) => {
        const message =
          error.response?.data?.message ?? i18n.t("misc.errorOccurred");

        Sentry.withScope((scope) => {
          scope.setContext("http", {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            status_code: error.response?.status,
            response_data: error.response?.data,
            error_message: error.message,
          });
          if (error.response?.status !== undefined) {
            scope.setTag("http.status_code", error.response.status);
          }
          Sentry.captureException(error);
        });

        console.error(message);
        throw new Error(message);
      }
    );

    return () => client.interceptors.response.eject(interceptorId);
  }, [client, i18n]);

  return client;
};

export { useAxiosClient };
